import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch (e) {
      // Sólo desconectar si el servidor RESPONDIÓ explícitamente con 401/403.
      // Chrome aborta peticiones in-flight al navegar rápido o cerrar tabs y
      // axios reporta esos casos como error de red (sin e.response). En esos
      // casos NO debemos borrar el token, sólo dejar la sesión como pendiente.
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("fc_token");
        setUser(null);
      }
      // Network/timeout/abort → conservamos el token; el siguiente intento volverá a verificar
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("fc_token");
    if (!token) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (identifier, password) => {
    // identifier: email o código (autodetectado por presencia de '@')
    const isEmail = (identifier || "").includes("@");
    const payload = isEmail
      ? { email: identifier, password }
      : { code: (identifier || "").toUpperCase(), password };
    const { data } = await api.post("/auth/login", payload);
    localStorage.setItem("fc_token", data.token);
    setUser({
      id: data.id,
      email: data.email,
      name: data.name,
      role: data.role,
      license_code: data.license_code,
      license_type: data.license_type,
      brand: data.brand || "",
      onboarded: data.onboarded,
      must_change_password: data.must_change_password,
    });
    return data;
  };

  const logout = () => {
    localStorage.removeItem("fc_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
